import React, { useEffect, useRef } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import { motion, useAnimation } from 'framer-motion';
import { AiFillAppstore } from 'react-icons/ai';
import { getPanelIconPath } from '../../../data/panelIconCatalog';
import { launcherHomeLabelSx, launcherIconLabelLayoutSx } from './launcherHomeLabelSx';
import HomeButtonImage from './HomeButtonImage';

const ICON_SIZES = {
  small: { icon: 60, fontSize: 20 },
  medium: { icon: 80, fontSize: 28 },
  large: { icon: 100, fontSize: 36 },
  xlarge: { icon: 120, fontSize: 44 },
};

export default function AppIcon({
  icon,
  editMode,
  suppressActivation = false,
  onLongPress,
  onClick,
  onEditRequest,
  onDeleteRequest,
  dark,
  dragHandleProps = {},
  iconSize = 'medium',
  /**
   * HomeLauncher Settings special-case. Configurable so Touch Panels can reuse
   * without treating id==='settings' as protected.
   * Default preserves current HomeLauncher behavior.
   */
  isProtected,
  /**
   * When true, label sits inside the rounded tile (device-like Touch Panel).
   * Default false keeps Home Launcher layout (glyph tile + caption below).
   */
  labelInside = false,
  /** Hide the corner delete control even when editMode is true. */
  hideDelete = false,
  /**
   * iOS-style wiggle while editing. Home Launcher keeps this on; Touch Panel
   * designer turns it off so tiles stay still while remaining draggable.
   */
  jiggle = true,
  /** Home screen runtime: white outlined caption readable over background images. */
  labelOverBackground = false,
  /** Tenant used to load a custom Home image. Omitted outside Home. */
  tenantId = '',
}) {
  const controls = useAnimation();
  const pressTimer = useRef(null);
  const longPressFired = useRef(false);
  const sizeConfig = ICON_SIZES[iconSize] || ICON_SIZES.medium;
  const protectedTile =
    typeof isProtected === 'boolean' ? isProtected : icon?.id === 'settings';
  const showSettingsGlyph = icon?.id === 'settings';
  const iconType = icon?.iconType || 'builtin';
  const builtinPath = getPanelIconPath(icon?.iconKey);
  const textValue = (icon?.iconText || '').toString().toUpperCase().slice(0, 5);
  const textBg = icon?.iconBg || (dark ? '#2b2f3a' : '#e6f0ff');

  useEffect(() => {
    if (editMode && jiggle) {
      controls.start({ rotate: [0, -2, 2, 0], transition: { repeat: Infinity, duration: 0.6 } });
    } else {
      controls.stop();
      controls.set({ rotate: 0 });
    }
  }, [editMode, jiggle, controls]);

  useEffect(() => {
    if (!suppressActivation) return;
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
    longPressFired.current = false;
  }, [suppressActivation]);

  const style = {
    cursor: editMode ? 'grab' : 'pointer',
    touchAction: 'none',
    width: '100%',
    height: '100%',
  };

  const handlePointerDown = (event) => {
    if (editMode) return;
    // Mouse/trackpad drag must not enter edit mode. Long-press edit is touch-only.
    const pointerType = event?.pointerType || event?.nativeEvent?.pointerType;
    if (pointerType !== 'touch') return;
    if (pressTimer.current) clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      onLongPress?.();
    }, 550);
  };

  const handlePointerMove = (event) => {
    if (!pressTimer.current) return;
    const dx = event.movementX || 0;
    const dy = event.movementY || 0;
    if ((dx * dx) + (dy * dy) >= 64) {
      clearTimer();
    }
  };

  const clearTimer = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  const handleClick = () => {
    if (suppressActivation) {
      clearTimer();
      longPressFired.current = false;
      return;
    }
    if (editMode) {
      if (!protectedTile) {
        onEditRequest?.(icon);
      }
      return;
    }
    if (longPressFired.current) {
      longPressFired.current = false;
      return;
    }
    clearTimer();
    onClick?.();
  };

  const handleContextMenu = (event) => {
    event.preventDefault();
    if (!protectedTile) {
      onEditRequest?.(icon);
    }
  };

  const glyphFontSize = `clamp(14px, 32cqi, ${sizeConfig.fontSize}px)`;
  const glyph = showSettingsGlyph ? (
    <SettingsIcon
      sx={{
        fontSize: glyphFontSize,
        color: dark ? '#b9c2ff' : '#3c5eca',
      }}
    />
  ) : iconType === 'text' ? (
    <Typography
      variant="h6"
      sx={{
        fontSize: glyphFontSize,
        fontWeight: 700,
        letterSpacing: 1,
        color: dark ? 'common.white' : 'common.black',
        textTransform: 'uppercase',
        lineHeight: 1,
      }}
    >
      {textValue || (icon?.label || '').slice(0, 3).toUpperCase()}
    </Typography>
  ) : iconType === 'image' && icon?.iconImageAssetId ? (
    <HomeButtonImage
      assetId={icon.iconImageAssetId}
      tenantId={tenantId}
      alt={icon.label}
      fallback={<AiFillAppstore size={sizeConfig.fontSize} color={dark ? '#b9c2ff' : '#3c5eca'} />}
    />
  ) : builtinPath ? (
    <Box
      component="img"
      src={builtinPath}
      alt={icon.label}
      sx={{
        width: labelInside ? '70%' : 'min(86%, 100%)',
        height: labelInside ? '70%' : 'min(86%, 100%)',
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
      }}
    />
  ) : (
    <AiFillAppstore size={sizeConfig.fontSize} color={dark ? '#b9c2ff' : '#3c5eca'} />
  );

  const deleteButton =
    editMode && !protectedTile && !hideDelete ? (
      <IconButton
        size="small"
        onPointerDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onDeleteRequest?.(icon);
        }}
        sx={{
          position: 'absolute',
          top: -8,
          left: -8,
          width: 26,
          height: 26,
          bgcolor: 'error.main',
          color: 'common.white',
          '&:hover': { bgcolor: 'error.dark' },
          boxShadow: 1,
          zIndex: 5,
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    ) : null;

  const labelSx = labelOverBackground && !labelInside
    ? { ...launcherIconLabelLayoutSx, ...launcherHomeLabelSx }
    : {
        ...launcherIconLabelLayoutSx,
        color: icon?.labelColor || (dark ? 'grey.300' : 'text.primary'),
      };

  if (labelInside) {
    return (
      <div
        onPointerDown={!editMode ? handlePointerDown : undefined}
        onPointerMove={!editMode ? handlePointerMove : undefined}
        onPointerUp={!editMode ? clearTimer : undefined}
        onPointerCancel={!editMode ? clearTimer : undefined}
        onPointerLeave={!editMode ? clearTimer : undefined}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        style={{ ...style, position: 'relative' }}
      >
        <Box
          component={motion.div}
          animate={controls}
          {...dragHandleProps}
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            minHeight: 0,
            borderRadius: 3,
            background:
              iconType === 'text'
                ? textBg
                : dark
                  ? 'linear-gradient(135deg, #2b2f3a, #0f1116)'
                  : 'linear-gradient(135deg, #e6f0ff, #c7d9ff)',
            boxShadow: dark
              ? '0 6px 16px rgba(0,0,0,0.45)'
              : '0 8px 18px rgba(0,0,0,0.12) inset',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            pt: 1,
            pb: 0.75,
            px: 0.75,
            boxSizing: 'border-box',
            userSelect: 'none',
            overflow: 'hidden',
            containerType: 'size',
            cursor: editMode ? 'grab' : 'pointer',
          }}
        >
          {deleteButton}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {glyph}
          </Box>
          <Typography variant="caption" sx={{ ...labelSx, flexShrink: 0, mt: 0.5 }}>
            {icon.label}
          </Typography>
        </Box>
      </div>
    );
  }

  return (
    <div
      onPointerDown={!editMode ? handlePointerDown : undefined}
      onPointerMove={!editMode ? handlePointerMove : undefined}
      onPointerUp={!editMode ? clearTimer : undefined}
      onPointerCancel={!editMode ? clearTimer : undefined}
      onPointerLeave={!editMode ? clearTimer : undefined}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      style={{ ...style, position: 'relative' }}
    >
      <Box
        component={motion.div}
        animate={controls}
        {...dragHandleProps}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'clamp(2px, 3cqi, 8px)',
          userSelect: 'none',
          minHeight: 0,
          overflow: 'hidden',
          containerType: 'size',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: `${sizeConfig.icon}px`,
            aspectRatio: '1 / 1',
            flexShrink: 1,
            minHeight: 0,
            minWidth: 0,
            borderRadius: 3,
            background:
              iconType === 'text'
                ? textBg
                : dark
                  ? 'linear-gradient(135deg, #2b2f3a, #0f1116)'
                  : 'linear-gradient(135deg, #e6f0ff, #c7d9ff)',
            boxShadow: dark
              ? '0 6px 16px rgba(0,0,0,0.45)'
              : '0 8px 18px rgba(0,0,0,0.12) inset',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: editMode ? 'grab' : 'pointer',
            margin: '0 auto',
          }}
        >
          {deleteButton}
          {glyph}
        </Box>
        <Typography variant="caption" sx={{ ...labelSx, flexShrink: 0 }}>
            {icon.label}
          </Typography>
      </Box>
    </div>
  );
}
